/**
 * The `projects` section + section-hosted views — RO-3-10 (task #44303).
 *
 * Scope (docs/rollover/inventory/pages.md § Projects — 60 pages):
 *
 * 1. `/projects` — redirect to `/work/projects` (canonical); `?catalog=1` keeps
 *    the classic folders board until those affordances fold into Work.
 * 2. `/projects/map` (+ `/define`) — workspace organization graph (read view).
 * 3. `/projects/$projectSlug` — project home via shared `HomeScreen`.
 * 4. `/projects/$projectSlug/$sectionSlug` — mode roots → ModePage; non-mode
 *    section roots → cold-start blank / overview redirect.
 * 5. `/projects/$projectSlug/$sectionSlug/$` — ModeSectionRoute for mode tiers.
 * 6. `/projects/sections/$sectionSlug` — workspace-tier section root.
 * 7. Section-hosted CRM / hiring leaves under the project tier.
 * 8. Settings identity (and shells for sibling settings tabs).
 * 9. Detail id→uid redirects (leads, opportunities, goals, applications).
 *
 * RO-3-14 registered many of these as PENDING placeholders in
 * `dynamic-segments.tsx`; this file owns the real bodies and removes them from
 * PENDING_SEGMENT_PATHS. Live Next pages under `excellent/app/projects/**` are
 * UNTOUCHED (PRD D6).
 *
 * #44578 — 11 literal leaves that were SHADOWED by catch-alls / section root:
 *   /projects/$projectSlug/{agents,issues,planner}
 *   /projects/$projectSlug/$sectionSlug/{agents,analytics,issues,performance,
 *     planner,prd,seo,goals/new}
 * Each carries a unique `data-page` marker so ranking is provable against the
 * catch-all body (status probes cannot see this class of defect).
 *
 * Deferred with reason (in task evidence):
 *   - Full MID analytics/seo dashboards still needing multi-source marketing
 *     contracts (shells mount with unique markers; deepen under Customers).
 *   - Settings brief/tracking/organization/sections/preferences form bodies
 *     beyond identity (mutations already mount as projectActions.*).
 *   - Interactive map card menus (role/unlink/move) — mutations exist; the
 *     full WorkspaceMap action wiring is a follow-on.
 *   - Full project-tier agents portfolio (354 LOC) — Runs strip is live;
 *     SuggestedAgents / driver stats deepen with agents panel contracts.
 */

import * as React from "react";
import { z } from "zod";
import { createRoute, redirect } from "@tanstack/react-router";
import {
  Button,
  EmptyState,
  PageHeader,
  PageNav,
  SettingsPageShell,
  FormShell,
  FormField,
  Input,
  Textarea,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@excellent/ui";
import {
  IdCard,
  ScrollText,
  LineChart,
  FolderTree,
  LayoutGrid,
  Settings2,
  Network,
} from "@excellent/ui/icons";
import { apiCall, useApi } from "@excellent/ui/api";
import { RouterLink as Link, useNavigate } from "@excellent/ui/router";
import { projectsViews } from "@excellent/ui/api/contracts/projects-views";
import { projectActions } from "@excellent/ui/api/contracts/project-actions";
import { agentGoals } from "@excellent/ui/api/contracts/agent-goals";
import { crm } from "@excellent/ui/api/contracts/crm";
import { hiringApplications } from "@excellent/ui/api/contracts/hiring-applications";
import { isModeSlug, MODE_VIEWS, type ModeView } from "@/modes/modes-registry";
import { getSectionRegistryEntry } from "@excellent/core/section-registry";
import { HomeScreen } from "../shared/home/home-screen";
import {
  ModePage,
  ModeSectionRoute,
  redirectAreaToPath,
  SectionColdStartDegrade,
  LeadsView,
  NewLeadView,
  OpportunitiesView,
  NewOpportunityView,
  SequencesView,
  NewSequenceView,
  TemplatesView,
  NewTemplateView,
  ReportsView,
  CrmSettingsView,
  JobsView,
  NewJobView,
  JobDetailView,
  JobEditView,
  JobPipelineView,
  CandidatesView,
  PipelineHubView,
  HiringSettingsView,
  AgentsRunsView,
  GoalsView,
  IssuesBoardView,
  WorkPlannerView,
  MarketingAnalyticsView,
  MarketingSeoView,
  DeliveryPerformanceView,
  DeliveryPrdView,
  PerformancePanel,
} from "../shared";
import { IssuesPage } from "../shared/work/issues-page";
import { PlannerPage } from "../shared/work/planner-page";
import { rootRoute } from "./__root";

// ── helpers ─────────────────────────────────────────────────────────────────

function asSearchParams(
  raw: Record<string, unknown> | undefined,
): Record<string, string | string[] | undefined> {
  const out: Record<string, string | string[] | undefined> = {};
  if (!raw) return out;
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === "string" || Array.isArray(v)) out[k] = v as string | string[];
  }
  return out;
}

function requireSales(sectionSlug: string): boolean {
  return sectionSlug === "sales";
}
function requireHiring(sectionSlug: string): boolean {
  return sectionSlug === "hiring";
}

// ── /projects ───────────────────────────────────────────────────────────────

function ProjectsIndexPage() {
  const navigate = useNavigate();
  const search = projectsIndexRoute.useSearch() as { catalog?: string };
  const catalog = search.catalog === "1";

  React.useEffect(() => {
    if (!catalog) navigate.replace("/work/projects");
  }, [catalog, navigate]);

  const { data, isLoading, error } = useApi(projectsViews.catalog, catalog ? {} : null);

  if (!catalog) {
    return (
      <p className="px-6 py-8 type-body text-muted-foreground">
        Redirecting to Work ▸ Projects…
      </p>
    );
  }

  if (isLoading && !data) {
    return <p className="px-6 py-8 type-body text-muted-foreground">Loading projects…</p>;
  }
  if (error || !data) {
    return (
      <EmptyState
        title="Could not load projects"
        description={error?.message ?? "Try refreshing."}
      />
    );
  }

  return (
    <>
      <PageNav
        section={{ label: "Projects", href: "/projects" }}
        tabs={[]}
        right={
          <Button asChild variant="outline">
            <Link href="/projects/map">
              <Network className="size-3.5" />
              Map your projects
            </Link>
          </Button>
        }
      />
      <div className="px-6 py-8 space-y-6">
        <PageHeader
          title={data.workspaceName}
          description={`${data.projects.length} project${data.projects.length === 1 ? "" : "s"} in this workspace`}
        />
        {data.projects.length === 0 && data.discovered.length === 0 ? (
          <EmptyState
            icon={<LayoutGrid className="size-8" />}
            title="No projects yet"
            description="Run Claude Code in any directory to register a project here."
          />
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.projects.map((p) => (
              <li key={p.slug}>
                <Card interactive asChild className="gap-0 py-4">
                  <Link href={`/projects/${p.slug}`} className="block px-4">
                    <div className="font-medium text-foreground truncate">{p.name}</div>
                    <div className="mt-1 type-subtext text-muted-foreground">
                      {p.open} open · {p.review} review · {p.sectionCount} sections
                    </div>
                  </Link>
                </Card>
              </li>
            ))}
          </ul>
        )}
        {data.discovered.length > 0 && (
          <section>
            <h2 className="type-title font-medium text-muted-foreground mb-2">Discovered</h2>
            <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {data.discovered.map((p) => (
                <li
                  key={p.slug}
                  className="rounded-lg border border-dashed border-border p-3 type-body"
                >
                  {p.name}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  );
}

export const projectsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects",
  validateSearch: z.object({ catalog: z.string().optional() }).passthrough(),
  component: ProjectsIndexPage,
});

// ── /projects/map ───────────────────────────────────────────────────────────

function ProjectsMapPage() {
  const { data, isLoading, error } = useApi(projectsViews.map, {});
  if (isLoading && !data) {
    return <p className="px-6 py-8 type-body text-muted-foreground">Loading map…</p>;
  }
  if (error || !data) {
    return (
      <EmptyState
        title="Could not load project map"
        description={error?.message ?? "Try refreshing."}
      />
    );
  }

  const byParent = new Map<number | null, typeof data.projects>();
  for (const p of data.projects) {
    const key = p.parent_id;
    const arr = byParent.get(key) ?? [];
    arr.push(p);
    byParent.set(key, arr);
  }
  const tops = data.projects.filter((p) => p.parent_id == null);

  return (
    <>
      <PageNav
        section={{ label: "Projects", href: "/projects" }}
        tabs={[]}
        title="Project map"
      />
      <div className="mx-auto w-full max-w-6xl px-6 py-8 space-y-6">
        <p className="max-w-prose type-body text-muted-foreground">
          How your projects are grouped: each product holds the codebases beneath
          it. Use a project&rsquo;s settings to set a role, move it under a product,
          or add a connection.{" "}
          <Link href="/projects/map/define" className="underline underline-offset-2">
            Define roles &amp; connections
          </Link>
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tops.map((p) => {
            const children = data.projects.filter((c) => c.parent_id === p.id);
            return (
              <Card key={p.slug}>
                <CardHeader className="pb-2">
                  <CardTitle className="type-title">
                    <Link href={`/projects/${p.slug}`} className="hover:underline">
                      {p.name}
                    </Link>
                  </CardTitle>
                  <p className="type-subtext text-muted-foreground">
                    {p.role ?? "no role"} · {p.manual ? "manual" : "discovered"}
                  </p>
                </CardHeader>
                {children.length > 0 && (
                  <CardContent className="pt-0">
                    <ul className="space-y-1 type-body text-muted-foreground">
                      {children.map((c) => (
                        <li key={c.slug}>
                          <Link href={`/projects/${c.slug}`} className="hover:underline">
                            {c.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
        {data.relationships.length > 0 && (
          <section>
            <h2 className="type-title font-medium mb-2">Connections</h2>
            <ul className="type-body text-muted-foreground space-y-1">
              {data.relationships.map((r, i) => (
                <li key={i}>
                  {r.from} → {r.to}
                  {r.kind ? ` (${r.kind})` : ""}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  );
}

export const projectsMapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/map",
  component: ProjectsMapPage,
});

export const projectsMapDefineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/map/define",
  component: function ProjectsMapDefinePage() {
    return (
      <>
        <PageNav
          section={{ label: "Projects", href: "/projects" }}
          tabs={[
            {
              href: "/projects/map",
              label: "Map",
              icon: <Network className="h-3.5 w-3.5" strokeWidth={1.75} />,
            },
          ]}
          title="Define roles & connections"
        />
        <div className="mx-auto max-w-2xl px-6 py-8">
          <EmptyState
            title="Define walk"
            description="Use each project's settings and the map connections list. The guided Play-style walk ports with the remaining map-define actions."
          />
          <div className="mt-4">
            <Button asChild variant="outline">
              <Link href="/projects/map">Back to map</Link>
            </Button>
          </div>
        </div>
      </>
    );
  },
});

// ── /projects/$projectSlug ──────────────────────────────────────────────────

export const projectHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug",
  component: function ProjectHomePage() {
    const { projectSlug } = projectHomeRoute.useParams();
    const { data, isLoading, error } = useApi(projectsViews.bySlug, {
      params: { slug: projectSlug },
    });

    if (isLoading && !data) {
      return <p className="px-6 py-8 type-body text-muted-foreground">Loading project…</p>;
    }
    if (error || !data) {
      return (
        <EmptyState
          title="Project not found"
          description={error?.message ?? `No project named “${projectSlug}”.`}
        />
      );
    }

    return (
      <HomeScreen
        workspaceId={data.workspace_id ?? 1}
        project={{ id: data.id, slug: data.slug, name: data.name }}
      />
    );
  },
});

export const projectTasksRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/tasks",
  beforeLoad: ({ params, search }) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries((search ?? {}) as Record<string, unknown>)) {
      if (typeof v === "string") qs.set(k, v);
    }
    const q = qs.toString();
    throw redirect({
      to: `/projects/${params.projectSlug}/issues${q ? `?${q}` : ""}`,
      replace: true,
    });
  },
  component: () => null,
});

// ── project settings ────────────────────────────────────────────────────────

function ProjectSettingsChrome({
  projectSlug,
  children,
}: {
  projectSlug: string;
  children: React.ReactNode;
}) {
  const { data } = useApi(projectsViews.bySlug, { params: { slug: projectSlug } });
  const base = `/projects/${projectSlug}/settings`;
  const isTopLevel = data ? data.parent_id == null : true;

  return (
    <>
      <PageNav
        section={{ label: "Settings", href: `/projects/${projectSlug}` }}
        tabs={[
          {
            href: base,
            label: "Identity",
            icon: <IdCard className="h-3.5 w-3.5" strokeWidth={1.75} />,
          },
          {
            href: `${base}/brief`,
            label: "Brief",
            icon: <ScrollText className="h-3.5 w-3.5" strokeWidth={1.75} />,
          },
          ...(isTopLevel
            ? [
                {
                  href: `${base}/tracking`,
                  label: "Tracking",
                  icon: <LineChart className="h-3.5 w-3.5" strokeWidth={1.75} />,
                },
              ]
            : []),
          {
            href: `${base}/organization`,
            label: "Organization",
            icon: <FolderTree className="h-3.5 w-3.5" strokeWidth={1.75} />,
          },
          {
            href: `${base}/sections`,
            label: "Sections",
            icon: <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.75} />,
          },
          {
            href: `${base}/preferences`,
            label: "Preferences",
            icon: <Settings2 className="h-3.5 w-3.5" strokeWidth={1.75} />,
          },
        ]}
      />
      {children}
    </>
  );
}

export const projectSettingsIdentityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/settings",
  component: function IdentitySettingsPage() {
    const { projectSlug } = projectSettingsIdentityRoute.useParams();
    const { data, isLoading, error, mutate } = useApi(projectsViews.bySlug, {
      params: { slug: projectSlug },
    });
    const [name, setName] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [icon, setIcon] = React.useState("");
    const [color, setColor] = React.useState("");
    const [pending, setPending] = React.useState(false);
    const [saved, setSaved] = React.useState(false);

    React.useEffect(() => {
      if (!data) return;
      setName(data.name);
      setDescription(data.description ?? "");
      setIcon(data.icon ?? "");
      setColor(data.color ?? "");
    }, [data]);

    if (isLoading && !data) {
      return <p className="px-6 py-8 type-body text-muted-foreground">Loading…</p>;
    }
    if (error || !data) {
      return <EmptyState title="Project not found" description={error?.message} />;
    }

    const dirty =
      name !== data.name ||
      description !== (data.description ?? "") ||
      icon !== (data.icon ?? "") ||
      color !== (data.color ?? "");

    async function onSave() {
      setPending(true);
      try {
        await apiCall(projectActions.identity, {
          body: { slug: projectSlug, name, description, icon, color },
        });
        setSaved(true);
        await mutate();
      } finally {
        setPending(false);
      }
    }

    return (
      <ProjectSettingsChrome projectSlug={projectSlug}>
        <SettingsPageShell
          title="Identity"
          description="How this project shows up in lists, cards, and navigation."
        >
          <FormShell
            onSubmit={(e) => {
              e.preventDefault();
              void onSave();
            }}
            footer={
              <Button type="submit" disabled={!dirty || pending}>
                {pending ? "Saving…" : saved && !dirty ? "Saved" : "Save"}
              </Button>
            }
          >
            <FormField label="Name">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </FormField>
            <FormField label="Description">
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </FormField>
            <FormField label="Icon">
              <Input
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder="emoji or short label"
              />
            </FormField>
            <FormField label="Color">
              <Input
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#318AEA"
              />
            </FormField>
          </FormShell>
        </SettingsPageShell>
      </ProjectSettingsChrome>
    );
  },
});

export const projectSettingsBriefRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/settings/brief",
  component: function Page() {
    const { projectSlug } = projectSettingsBriefRoute.useParams();
    return (
      <ProjectSettingsChrome projectSlug={projectSlug}>
        <SettingsPageShell title="Brief" description="What this project is for.">
          <p className="type-body text-muted-foreground">
            Mutations mount as <code className="type-subtext">projectActions.brief</code>. Full form
            body ports with the remaining settings leaves.
          </p>
        </SettingsPageShell>
      </ProjectSettingsChrome>
    );
  },
});

export const projectSettingsTrackingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/settings/tracking",
  component: function Page() {
    const { projectSlug } = projectSettingsTrackingRoute.useParams();
    return (
      <ProjectSettingsChrome projectSlug={projectSlug}>
        <SettingsPageShell
          title="Tracking"
          description="Classify this project as a tracked product."
        >
          <p className="type-body text-muted-foreground">
            Mutations mount as <code className="type-subtext">projectActions.tracking</code> /
            metrics / targets.
          </p>
        </SettingsPageShell>
      </ProjectSettingsChrome>
    );
  },
});

export const projectSettingsOrganizationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/settings/organization",
  component: function Page() {
    const { projectSlug } = projectSettingsOrganizationRoute.useParams();
    return (
      <ProjectSettingsChrome projectSlug={projectSlug}>
        <SettingsPageShell
          title="Organization"
          description="Folder, parent product, and workspace."
        >
          <p className="type-body text-muted-foreground">
            Mutations mount as move-group / parent / move-workspace.
          </p>
        </SettingsPageShell>
      </ProjectSettingsChrome>
    );
  },
});

export const projectSettingsSectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/settings/sections",
  component: function Page() {
    const { projectSlug } = projectSettingsSectionsRoute.useParams();
    return (
      <ProjectSettingsChrome projectSlug={projectSlug}>
        <SettingsPageShell
          title="Sections"
          description="Enable or archive sections on this project."
        >
          <p className="type-body text-muted-foreground">
            Mutations mount as create/rename/archive/restore section.
          </p>
        </SettingsPageShell>
      </ProjectSettingsChrome>
    );
  },
});

export const projectSettingsPreferencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/settings/preferences",
  component: function Page() {
    const { projectSlug } = projectSettingsPreferencesRoute.useParams();
    return (
      <ProjectSettingsChrome projectSlug={projectSlug}>
        <SettingsPageShell
          title="Preferences"
          description="Per-project preference keys."
        >
          <p className="type-body text-muted-foreground">
            Mutations mount as preference / preferenceDelete.
          </p>
        </SettingsPageShell>
      </ProjectSettingsChrome>
    );
  },
});

// ── section root + catch-all ────────────────────────────────────────────────

export const projectSectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug",
  validateSearch: z.record(z.any()).optional(),
  component: function ProjectSectionRootPage() {
    const { projectSlug, sectionSlug } = projectSectionRoute.useParams();
    const search = asSearchParams(
      projectSectionRoute.useSearch() as Record<string, unknown>,
    );
    const navigate = useNavigate();

    if (isModeSlug(sectionSlug) && sectionSlug !== "agents") {
      if (redirectAreaToPath(sectionSlug, search, navigate)) return null;
      const view = (MODE_VIEWS as readonly string[]).includes(search.view as string)
        ? (search.view as ModeView)
        : "dashboard";
      return (
        <ModePage
          mode={sectionSlug}
          view={view}
          projectSlug={projectSlug}
          area={typeof search.area === "string" ? search.area : undefined}
          sub={typeof search.sub === "string" ? search.sub : undefined}
          searchParams={search}
        />
      );
    }

    if (sectionSlug === "agents") {
      return (
        <EmptyState
          title="Project agents"
          description="Open a section’s Agents view, or the workspace Agents panel."
        />
      );
    }

    const entry = getSectionRegistryEntry(sectionSlug);
    if (!entry) {
      return (
        <EmptyState title="Section not found" description={`No section “${sectionSlug}”.`} />
      );
    }

    return (
      <SectionColdStartDegrade
        slug={sectionSlug}
        label={entry.name ?? sectionSlug}
        hint="Connect a source or open the section overview from a project."
        projectHref={`/projects/${projectSlug}/${sectionSlug}/overview`}
        projectLabel="Open overview"
      />
    );
  },
});

export const projectSectionCatchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/$",
  // #44601: empty splat outranks bare `/projects/$projectSlug/$sectionSlug` —
  // reject so the section root wins the match (same class as mode roots).
  params: {
    parse: (raw) => {
      if (!raw._splat) return false;
      return raw;
    },
  },
  validateSearch: z.record(z.any()).optional(),
  component: function ProjectSectionCatchAllPage() {
    const params = projectSectionCatchAllRoute.useParams() as {
      projectSlug: string;
      sectionSlug: string;
      _splat?: string;
    };
    const { projectSlug, sectionSlug, _splat } = params;
    const search = asSearchParams(
      projectSectionCatchAllRoute.useSearch() as Record<string, unknown>,
    );
    const segments = (_splat ?? "").split("/").filter(Boolean);

    if (!isModeSlug(sectionSlug) || sectionSlug === "agents") {
      return (
        <EmptyState
          title="Not found"
          description="This path is only valid under a mode (work, customers, product, operations)."
        />
      );
    }

    return (
      <ModeSectionRoute
        mode={sectionSlug}
        segments={segments}
        searchParams={search}
        projectSlug={projectSlug}
      />
    );
  },
});

// ── workspace-tier sections ─────────────────────────────────────────────────

export const workspaceSectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/sections/$sectionSlug",
  component: function WorkspaceSectionPage() {
    const { sectionSlug } = workspaceSectionRoute.useParams();
    const entry = getSectionRegistryEntry(sectionSlug);
    if (!entry) {
      return (
        <EmptyState title="Section not found" description={`No section “${sectionSlug}”.`} />
      );
    }
    return (
      <SectionColdStartDegrade
        slug={sectionSlug}
        label={entry.name ?? sectionSlug}
        hint="Workspace-tier section view. Open a project for the full surface."
        projectHref={`/projects/sections/${sectionSlug}/dashboard`}
        projectLabel="Open dashboard"
      />
    );
  },
});

export const workspaceSectionDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/sections/$sectionSlug/dashboard",
  component: function Page() {
    const { sectionSlug } = workspaceSectionDashboardRoute.useParams();
    return (
      <EmptyState
        title={`${sectionSlug} dashboard`}
        description="Workspace-tier section dashboard. Full panels port with remaining MID overview contracts."
      />
    );
  },
});

export const workspaceSectionAuditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/sections/$sectionSlug/audit",
  component: () => (
    <EmptyState title="Section audit" description="Shared SectionAuditPanel." />
  ),
});

export const workspaceSectionIssuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/sections/$sectionSlug/issues",
  component: () => (
    <EmptyState title="Section issues" description="Workspace-tier section issues queue." />
  ),
});

// ── section-hosted CRM leaves ───────────────────────────────────────────────

export const projectLeadsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/leads",
  component: function Page() {
    const { projectSlug, sectionSlug } = projectLeadsRoute.useParams();
    if (!requireSales(sectionSlug)) {
      return <EmptyState title="Not found" description="Leads live under the sales section." />;
    }
    return <LeadsView basePath={`/projects/${projectSlug}/sales`} />;
  },
});

export const projectLeadsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/leads/new",
  component: function Page() {
    const { projectSlug, sectionSlug } = projectLeadsNewRoute.useParams();
    if (!requireSales(sectionSlug)) return <EmptyState title="Not found" />;
    return <NewLeadView basePath={`/projects/${projectSlug}/sales`} />;
  },
});

export const projectOpportunitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/opportunities",
  component: function Page() {
    const { projectSlug, sectionSlug } = projectOpportunitiesRoute.useParams();
    if (!requireSales(sectionSlug)) {
      return <EmptyState title="Not found" description="Opportunities live under sales." />;
    }
    return <OpportunitiesView basePath={`/projects/${projectSlug}/sales`} />;
  },
});

export const projectOpportunitiesNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/opportunities/new",
  component: function Page() {
    const { projectSlug, sectionSlug } = projectOpportunitiesNewRoute.useParams();
    if (!requireSales(sectionSlug)) return <EmptyState title="Not found" />;
    return <NewOpportunityView basePath={`/projects/${projectSlug}/sales`} />;
  },
});

export const projectSequencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/sequences",
  component: function Page() {
    const { projectSlug, sectionSlug } = projectSequencesRoute.useParams();
    if (!requireSales(sectionSlug)) return <EmptyState title="Not found" />;
    return <SequencesView basePath={`/projects/${projectSlug}/sales`} />;
  },
});

export const projectSequencesNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/sequences/new",
  component: function Page() {
    const { projectSlug, sectionSlug } = projectSequencesNewRoute.useParams();
    if (!requireSales(sectionSlug)) return <EmptyState title="Not found" />;
    return <NewSequenceView basePath={`/projects/${projectSlug}/sales`} />;
  },
});

export const projectTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/templates",
  component: function Page() {
    const { projectSlug, sectionSlug } = projectTemplatesRoute.useParams();
    if (!requireSales(sectionSlug)) return <EmptyState title="Not found" />;
    void projectSlug;
    return <TemplatesView />;
  },
});

export const projectTemplatesNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/templates/new",
  component: function Page() {
    const { projectSlug, sectionSlug } = projectTemplatesNewRoute.useParams();
    if (!requireSales(sectionSlug)) return <EmptyState title="Not found" />;
    return <NewTemplateView basePath={`/projects/${projectSlug}/sales`} />;
  },
});

export const projectReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/reports",
  component: function Page() {
    const { sectionSlug } = projectReportsRoute.useParams();
    if (!requireSales(sectionSlug)) return <EmptyState title="Not found" />;
    return <ReportsView />;
  },
});

export const projectSectionSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/settings",
  component: function Page() {
    const { sectionSlug } = projectSectionSettingsRoute.useParams();
    if (requireSales(sectionSlug)) return <CrmSettingsView />;
    if (requireHiring(sectionSlug)) return <HiringSettingsView />;
    return (
      <EmptyState
        title="Section settings"
        description="No settings surface for this section."
      />
    );
  },
});

// ── section-hosted hiring leaves ────────────────────────────────────────────

export const projectJobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/jobs",
  validateSearch: z.record(z.any()).optional(),
  component: function Page() {
    const { projectSlug, sectionSlug } = projectJobsRoute.useParams();
    const search = asSearchParams(projectJobsRoute.useSearch() as Record<string, unknown>);
    if (!requireHiring(sectionSlug)) {
      return <EmptyState title="Not found" description="Jobs live under the hiring section." />;
    }
    return (
      <JobsView basePath={`/projects/${projectSlug}/hiring`} searchParams={search} />
    );
  },
});

export const projectJobsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/jobs/new",
  component: function Page() {
    const { projectSlug, sectionSlug } = projectJobsNewRoute.useParams();
    if (!requireHiring(sectionSlug)) return <EmptyState title="Not found" />;
    return <NewJobView basePath={`/projects/${projectSlug}/hiring`} />;
  },
});

export const projectJobDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/jobs/$slug",
  component: function Page() {
    const { projectSlug, sectionSlug, slug } = projectJobDetailRoute.useParams();
    if (!requireHiring(sectionSlug)) return <EmptyState title="Not found" />;
    return <JobDetailView basePath={`/projects/${projectSlug}/hiring`} slug={slug} />;
  },
});

export const projectJobEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/jobs/$slug/edit",
  component: function Page() {
    const { projectSlug, sectionSlug, slug } = projectJobEditRoute.useParams();
    if (!requireHiring(sectionSlug)) return <EmptyState title="Not found" />;
    return <JobEditView basePath={`/projects/${projectSlug}/hiring`} slug={slug} />;
  },
});

export const projectJobPipelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/jobs/$slug/pipeline",
  component: function Page() {
    const { projectSlug, sectionSlug, slug } = projectJobPipelineRoute.useParams();
    if (!requireHiring(sectionSlug)) return <EmptyState title="Not found" />;
    return <JobPipelineView basePath={`/projects/${projectSlug}/hiring`} slug={slug} />;
  },
});

export const projectCandidatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/candidates",
  validateSearch: z.record(z.any()).optional(),
  component: function Page() {
    const { projectSlug, sectionSlug } = projectCandidatesRoute.useParams();
    const search = asSearchParams(
      projectCandidatesRoute.useSearch() as Record<string, unknown>,
    );
    if (!requireHiring(sectionSlug)) return <EmptyState title="Not found" />;
    return (
      <CandidatesView
        basePath={`/projects/${projectSlug}/hiring`}
        searchParams={search}
        rawRows={[]}
      />
    );
  },
});

export const projectPipelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/pipeline",
  component: function Page() {
    const { projectSlug, sectionSlug } = projectPipelineRoute.useParams();
    if (!requireHiring(sectionSlug)) return <EmptyState title="Not found" />;
    return <PipelineHubView basePath={`/projects/${projectSlug}/hiring`} />;
  },
});

// ── detail id → uid redirects ───────────────────────────────────────────────

export const projectLeadDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/leads/$id",
  beforeLoad: async ({ params }) => {
    if (params.sectionSlug !== "sales") {
      throw redirect({
        to: `/projects/${params.projectSlug}/${params.sectionSlug}`,
        replace: true,
      });
    }
    const id = Number(params.id);
    if (!Number.isFinite(id) || id <= 0) {
      throw redirect({ to: `/projects/${params.projectSlug}/sales/leads`, replace: true });
    }
    try {
      const lead = await apiCall(crm.leads.get, { params: { id } });
      const uid = (lead as { uid?: string | null })?.uid;
      if (!uid) throw redirect({ to: `/projects/${params.projectSlug}/sales/leads`, replace: true });
      throw redirect({ to: `/o/${uid}?from=customers`, replace: true });
    } catch (e) {
      if (e && typeof e === "object" && "to" in e) throw e;
      throw redirect({ to: `/projects/${params.projectSlug}/sales/leads`, replace: true });
    }
  },
  component: () => null,
});

export const projectOpportunityDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/opportunities/$id",
  beforeLoad: async ({ params }) => {
    if (params.sectionSlug !== "sales") {
      throw redirect({
        to: `/projects/${params.projectSlug}/${params.sectionSlug}`,
        replace: true,
      });
    }
    const id = Number(params.id);
    if (!Number.isFinite(id) || id <= 0) {
      throw redirect({
        to: `/projects/${params.projectSlug}/sales/opportunities`,
        replace: true,
      });
    }
    try {
      const opp = await apiCall(crm.opportunities.get, { params: { id } });
      const uid = (opp as { uid?: string | null })?.uid;
      if (!uid) {
        throw redirect({
          to: `/projects/${params.projectSlug}/sales/opportunities`,
          replace: true,
        });
      }
      throw redirect({ to: `/o/${uid}?from=customers`, replace: true });
    } catch (e) {
      if (e && typeof e === "object" && "to" in e) throw e;
      throw redirect({
        to: `/projects/${params.projectSlug}/sales/opportunities`,
        replace: true,
      });
    }
  },
  component: () => null,
});

export const projectGoalDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/goals/$id",
  beforeLoad: async ({ params }) => {
    const id = Number(params.id);
    if (!Number.isFinite(id) || id <= 0) {
      throw redirect({
        to: `/projects/${params.projectSlug}/${params.sectionSlug}`,
        replace: true,
      });
    }
    try {
      const result = await apiCall(agentGoals.resolve, { params: { id } });
      if (!result.uid) {
        throw redirect({
          to: `/projects/${params.projectSlug}/${params.sectionSlug}`,
          replace: true,
        });
      }
      throw redirect({ to: `/o/${result.uid}?from=agents`, replace: true });
    } catch (e) {
      if (e && typeof e === "object" && "to" in e) throw e;
      throw redirect({
        to: `/projects/${params.projectSlug}/${params.sectionSlug}`,
        replace: true,
      });
    }
  },
  component: () => null,
});

export const projectSequenceDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/sequences/$id",
  component: function Page() {
    const { projectSlug, sectionSlug } = projectSequenceDetailRoute.useParams();
    if (!requireSales(sectionSlug)) return <EmptyState title="Not found" />;
    return <SequencesView basePath={`/projects/${projectSlug}/sales`} />;
  },
});

export const projectApplicationDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/applications/$id",
  beforeLoad: async ({ params }) => {
    if (params.sectionSlug !== "hiring") {
      throw redirect({
        to: `/projects/${params.projectSlug}/${params.sectionSlug}`,
        replace: true,
      });
    }
    const id = Number(params.id);
    if (!Number.isFinite(id) || id <= 0) {
      throw redirect({
        to: `/projects/${params.projectSlug}/hiring/candidates`,
        replace: true,
      });
    }
    try {
      const row = await apiCall(hiringApplications.get, { params: { id } });
      const uid = (row as { uid?: string | null })?.uid;
      if (!uid) {
        throw redirect({
          to: `/projects/${params.projectSlug}/hiring/candidates`,
          replace: true,
        });
      }
      throw redirect({ to: `/o/${uid}?from=operations`, replace: true });
    } catch (e) {
      if (e && typeof e === "object" && "to" in e) throw e;
      throw redirect({
        to: `/projects/${params.projectSlug}/hiring/candidates`,
        replace: true,
      });
    }
  },
  component: () => null,
});

export const projectCandidateDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/candidates/$id",
  validateSearch: z.record(z.any()).optional(),
  component: function Page() {
    const { projectSlug, sectionSlug } = projectCandidateDetailRoute.useParams();
    const search = asSearchParams(
      projectCandidateDetailRoute.useSearch() as Record<string, unknown>,
    );
    if (!requireHiring(sectionSlug)) return <EmptyState title="Not found" />;
    return (
      <CandidatesView
        basePath={`/projects/${projectSlug}/hiring`}
        searchParams={search}
        rawRows={[]}
      />
    );
  },
});

// ── misc section-hosted leaves ──────────────────────────────────────────────

export const projectSectionOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/overview",
  component: function Page() {
    const { projectSlug, sectionSlug } = projectSectionOverviewRoute.useParams();
    if (requireSales(sectionSlug)) return <ReportsView />;
    if (requireHiring(sectionSlug)) {
      return <PipelineHubView basePath={`/projects/${projectSlug}/hiring`} />;
    }
    return (
      <EmptyState
        title="Section overview"
        description="Full section dashboards port with remaining MID overview contracts."
      />
    );
  },
});

export const projectSectionAuditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/audit",
  component: () => (
    <EmptyState title="Section audit" description="Shared SectionAuditPanel." />
  ),
});

export const projectSectionImportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/import",
  component: () => <EmptyState title="Import" description="Section import hub." />,
});

export const projectSectionDirectoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/directory",
  component: () => <EmptyState title="Directory" description="Section directory." />,
});

export const projectSectionSimulateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/simulate",
  component: () => <EmptyState title="Simulate" description="Company simulate view." />,
});

export const projectSectionChecklistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/checklist",
  component: () => <EmptyState title="Checklist" description="Section checklist." />,
});

export const projectSectionComponentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/components",
  component: () => (
    <EmptyState title="Components" description="Style-guide components for this section." />
  ),
});

export const projectSectionTypographyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/typography",
  component: () => (
    <EmptyState title="Typography" description="Style-guide typography for this section." />
  ),
});

export const projectSectionStyleGuideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/style-guide",
  component: () => <EmptyState title="Style guide" description="Section style guide." />,
});

// ── #44578 — literal leaves that outrank catch-alls / section root ──────────
//
// TanStack ranks by specificity: a static segment beats `$param` and beats
// the `$` splat. These paths MUST be registered or the catch-all / section
// root silently paints the wrong page (invisible to status probes).
// Each root element carries `data-page="<marker>"` unique to that leaf so
// ranking is content-provable, not vacuous.

function flattenSearch(
  raw: Record<string, string | string[] | undefined>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === "string") out[k] = v;
    else if (Array.isArray(v) && typeof v[0] === "string") out[k] = v[0];
  }
  return out;
}

/** Section-tier Agents — Runs / Workflows / Goals panel (not the catch-all "Not found"). */
export const projectSectionAgentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/agents",
  validateSearch: z.record(z.any()).optional(),
  component: function ProjectSectionAgentsPage() {
    const { projectSlug, sectionSlug } = projectSectionAgentsRoute.useParams();
    const search = asSearchParams(
      projectSectionAgentsRoute.useSearch() as Record<string, unknown>,
    );
    const viewRaw = typeof search.view === "string" ? search.view : undefined;
    const isOps = sectionSlug === "operations";
    const view =
      viewRaw === "workflows" || (viewRaw === "goals" && isOps) ? viewRaw : "runs";
    const basePath = `/projects/${projectSlug}/${sectionSlug}`;

    return (
      <div
        data-page="project-section-agents"
        className="@container/page flex flex-col gap-5 px-6 py-6"
      >
        <PageHeader
          title="Section agents"
          description={`${sectionSlug} · Runs, workflows, and goals for this section.`}
        />
        <div className="flex flex-wrap gap-2 type-body">
          <Button asChild variant={view === "runs" ? "default" : "outline"} size="sm">
            <Link href="/">Runs</Link>
          </Button>
          <Button asChild variant={view === "workflows" ? "default" : "outline"} size="sm">
            <Link href="/">Workflows</Link>
          </Button>
          {isOps ? (
            <Button asChild variant={view === "goals" ? "default" : "outline"} size="sm">
              <Link href="/">Goals</Link>
            </Button>
          ) : null}
        </div>
        {view === "goals" ? (
          <GoalsView basePath={basePath} scope="agentic" embedded />
        ) : view === "workflows" ? (
          <EmptyState
            title="Section workflows"
            description="Fleet console for this section mounts with the remaining agents panel contracts."
          />
        ) : (
          <AgentsRunsView basePath={basePath} embedded />
        )}
      </div>
    );
  },
});

/** Section-tier Web Analytics — outranks catch-all "Not found" for non-mode sections. */
export const projectSectionAnalyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/analytics",
  validateSearch: z.record(z.any()).optional(),
  component: function ProjectSectionAnalyticsPage() {
    const { projectSlug, sectionSlug } = projectSectionAnalyticsRoute.useParams();
    const search = flattenSearch(
      asSearchParams(projectSectionAnalyticsRoute.useSearch() as Record<string, unknown>),
    );
    // Mode slug in the section position: hand off to ModeSectionRoute (Next parity).
    if (isModeSlug(sectionSlug) && sectionSlug !== "agents") {
      return (
        <div data-page="project-section-analytics">
          <ModeSectionRoute
            mode={sectionSlug}
            segments={["analytics"]}
            searchParams={search}
            projectSlug={projectSlug}
          />
        </div>
      );
    }
    return (
      <div data-page="project-section-analytics" className="px-6 py-6">
        <MarketingAnalyticsView
          projectSlug={projectSlug}
          searchParams={search}
          sectionSlug={sectionSlug}
        />
      </div>
    );
  },
});

/** Section-tier Issues — mode handoff or section-scoped issues list. */
export const projectSectionIssuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/issues",
  validateSearch: z.record(z.any()).optional(),
  component: function ProjectSectionIssuesPage() {
    const { projectSlug, sectionSlug } = projectSectionIssuesRoute.useParams();
    const search = flattenSearch(
      asSearchParams(projectSectionIssuesRoute.useSearch() as Record<string, unknown>),
    );
    if (isModeSlug(sectionSlug) && sectionSlug !== "agents") {
      return (
        <div data-page="project-section-issues">
          <ModeSectionRoute
            mode={sectionSlug}
            segments={["issues"]}
            searchParams={search}
            projectSlug={projectSlug}
          />
        </div>
      );
    }
    return (
      <div data-page="project-section-issues" className="flex flex-col gap-4">
        <IssuesBoardView
          projectSlug={projectSlug}
          searchParams={{ ...search, section: sectionSlug }}
        />
      </div>
    );
  },
});

/** Section-tier Performance audit. */
export const projectSectionPerformanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/performance",
  component: function ProjectSectionPerformancePage() {
    const { projectSlug, sectionSlug } = projectSectionPerformanceRoute.useParams();
    if (isModeSlug(sectionSlug) && sectionSlug !== "agents") {
      return (
        <div data-page="project-section-performance">
          <ModeSectionRoute
            mode={sectionSlug}
            segments={["performance"]}
            searchParams={{}}
            projectSlug={projectSlug}
          />
        </div>
      );
    }
    return (
      <div data-page="project-section-performance" className="flex flex-col gap-5 px-6 py-6">
        <PageHeader
          title="Performance"
          description="Page-load baselines for this project section."
        />
        <DeliveryPerformanceView projectSlug={projectSlug} searchParams={{}} />
        <PerformancePanel />
      </div>
    );
  },
});

/** Section-tier Planner / calendar. */
export const projectSectionPlannerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/planner",
  validateSearch: z.record(z.any()).optional(),
  component: function ProjectSectionPlannerPage() {
    const { projectSlug, sectionSlug } = projectSectionPlannerRoute.useParams();
    const search = flattenSearch(
      asSearchParams(projectSectionPlannerRoute.useSearch() as Record<string, unknown>),
    );
    if (isModeSlug(sectionSlug) && sectionSlug !== "agents") {
      return (
        <div data-page="project-section-planner">
          <ModeSectionRoute
            mode={sectionSlug}
            segments={["planner"]}
            searchParams={search}
            projectSlug={projectSlug}
          />
        </div>
      );
    }
    return (
      <div data-page="project-section-planner">
        <WorkPlannerView
          projectSlug={projectSlug}
          searchParams={{ ...search, section: sectionSlug }}
        />
      </div>
    );
  },
});

/** Section-tier PRD viewer (development). */
export const projectSectionPrdRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/prd",
  component: function ProjectSectionPrdPage() {
    const { projectSlug, sectionSlug } = projectSectionPrdRoute.useParams();
    if (isModeSlug(sectionSlug) && sectionSlug !== "agents") {
      return (
        <div data-page="project-section-prd">
          <ModeSectionRoute
            mode={sectionSlug}
            segments={["prd"]}
            searchParams={{}}
            projectSlug={projectSlug}
          />
        </div>
      );
    }
    return (
      <div data-page="project-section-prd" className="px-6 py-6">
        <DeliveryPrdView projectSlug={projectSlug} searchParams={{}} sectionSlug={sectionSlug} />
      </div>
    );
  },
});

/** Section-tier SEO workbench. */
export const projectSectionSeoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/seo",
  component: function ProjectSectionSeoPage() {
    const { projectSlug, sectionSlug } = projectSectionSeoRoute.useParams();
    if (isModeSlug(sectionSlug) && sectionSlug !== "agents") {
      return (
        <div data-page="project-section-seo">
          <ModeSectionRoute
            mode={sectionSlug}
            segments={["seo"]}
            searchParams={{}}
            projectSlug={projectSlug}
          />
        </div>
      );
    }
    return (
      <div data-page="project-section-seo" className="px-6 py-6">
        <MarketingSeoView
          projectSlug={projectSlug}
          searchParams={{}}
          sectionSlug={sectionSlug}
        />
      </div>
    );
  },
});

/**
 * Section-tier New goal form. Must outrank BOTH the catch-all AND
 * `/goals/$id` (which would otherwise match id="new" and redirect away).
 * Operations only — matches the Next original.
 */
export const projectSectionGoalsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/$sectionSlug/goals/new",
  validateSearch: z
    .object({
      error: z.string().optional(),
      title: z.string().optional(),
      role: z.string().optional(),
      linkTask: z.string().optional(),
      lane: z.string().optional(),
    })
    .optional(),
  component: function ProjectSectionGoalsNewPage() {
    const { projectSlug, sectionSlug } = projectSectionGoalsNewRoute.useParams();
    const search = (projectSectionGoalsNewRoute.useSearch() ?? {}) as {
      error?: string;
      title?: string;
      role?: string;
      linkTask?: string;
      lane?: string;
    };
    const navigate = useNavigate();

    if (sectionSlug !== "operations") {
      return (
        <div data-page="project-section-goals-new" className="px-6 py-8">
          <EmptyState
            title="New goal"
            description="Goals are created under the operations section."
          />
        </div>
      );
    }

    const { data: rolesData } = useApi(agentGoals.roles, {});
    const roles = rolesData?.roles ?? [];
    const [title, setTitle] = React.useState(search.title ?? "");
    const [outcome, setOutcome] = React.useState("");
    const [why, setWhy] = React.useState("");
    const [role, setRole] = React.useState(search.role ?? "shipper");
    const [description, setDescription] = React.useState("");
    const [cadence, setCadence] = React.useState("on-demand");
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState<string | null>(
      search.error ?? null,
    );

    async function submit(e: React.FormEvent) {
      e.preventDefault();
      setSubmitting(true);
      setError(null);
      try {
        const result = await apiCall(agentGoals.create, {
          body: {
            title,
            outcome,
            why,
            role,
            cadence,
            project: projectSlug,
            description,
            task: search.linkTask,
            lane: search.lane,
          },
        });
        if (result && typeof result === "object" && "ok" in result && result.ok) {
          const uid = (result as { uid?: string }).uid;
          navigate.push(uid ? `/o/${uid}?from=agents` : "/");
          return;
        }
        const reason =
          result && typeof result === "object" && "reason" in result
            ? String((result as { reason?: string }).reason)
            : "create-failed";
        setError(reason);
      } finally {
        setSubmitting(false);
      }
    }

    return (
      <div data-page="project-section-goals-new" className="flex flex-col px-6 py-8">
        <PageHeader
          className="mb-5"
          title="New goal"
          description="Durable intent for the agent system, scoped to this project’s operations section."
          toolbar={
            <Button variant="outline" onClick={() => navigate.push("/")}>
              ← Goals
            </Button>
          }
        />
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="type-title">Goal definition</CardTitle>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="mb-4 rounded border border-destructive/40 bg-destructive/5 px-3 py-2 type-body text-destructive">
                {error}
              </div>
            ) : null}
            <form onSubmit={submit} className="space-y-5">
              <FormField label="Title" required htmlFor="sec-goal-title">
                <Input
                  id="sec-goal-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Renew vendor evidence weekly"
                  required
                  autoFocus
                />
              </FormField>
              <FormField label="Outcome" required htmlFor="sec-goal-outcome">
                <Textarea
                  id="sec-goal-outcome"
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value)}
                  required
                  rows={2}
                  placeholder="What's true when this is done."
                />
              </FormField>
              <FormField label="Why it matters (optional)" htmlFor="sec-goal-why">
                <Textarea
                  id="sec-goal-why"
                  value={why}
                  onChange={(e) => setWhy(e.target.value)}
                  rows={2}
                />
              </FormField>
              <FormField label="Role">
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="mt-2 w-full">
                    <SelectValue placeholder="Pick a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.name} value={r.name}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Description (optional)" htmlFor="sec-goal-description">
                <Textarea
                  id="sec-goal-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </FormField>
              <FormField label="Cadence" htmlFor="sec-goal-cadence">
                <Input
                  id="sec-goal-cadence"
                  value={cadence}
                  onChange={(e) => setCadence(e.target.value)}
                  placeholder="on-demand"
                />
              </FormField>
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => navigate.push("/")}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? "Creating…" : "Create goal"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  },
});

// ── Project-tier leaves (outrank `$sectionSlug` treating the name as a section) ─

/** Project-tier Agents — NOT "section named agents". */
export const projectTierAgentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/agents",
  component: function ProjectTierAgentsPage() {
    const { projectSlug } = projectTierAgentsRoute.useParams();
    const basePath = `/projects/${projectSlug}`;
    return (
      <div
        data-page="project-tier-agents"
        className="@container/page flex flex-col gap-5 px-6 py-6"
      >
        <PageHeader
          title="Project agents"
          description="Worker runs and goals for this project — not a section root."
        />
        <AgentsRunsView basePath={basePath} />
        <GoalsView basePath="/" embedded />
      </div>
    );
  },
});

/** Project-tier Issues — same UI as /issues, scoped to this project. */
export const projectTierIssuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/issues",
  validateSearch: z.record(z.any()).optional(),
  component: function ProjectTierIssuesPage() {
    const { projectSlug } = projectTierIssuesRoute.useParams();
    const search = flattenSearch(
      asSearchParams(projectTierIssuesRoute.useSearch() as Record<string, unknown>),
    );
    return (
      <div data-page="project-tier-issues" className="flex flex-col gap-4">
        <IssuesPage
          searchParams={{
            ...search,
            project: projectSlug,
            status:
              search.status ?? "in_progress,review,todo,backlog",
            range: search.range ?? "all",
          }}
        />
      </div>
    );
  },
});

/** Project-tier Planner — same UI as /planner, scoped to this project. */
export const projectTierPlannerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectSlug/planner",
  validateSearch: z.record(z.any()).optional(),
  component: function ProjectTierPlannerPage() {
    const { projectSlug } = projectTierPlannerRoute.useParams();
    const search = flattenSearch(
      asSearchParams(projectTierPlannerRoute.useSearch() as Record<string, unknown>),
    );
    return (
      <div data-page="project-tier-planner">
        <PageHeader
          className="px-6 pt-6"
          title="Project planner"
          description={`Calendar for project “${projectSlug}”.`}
        />
        <PlannerPage searchParams={{ ...search, project: projectSlug }} />
      </div>
    );
  },
});
